// ============================================================
// ClientAppointments.tsx
// Historial de citas del cliente con filtros, paginación,
// valoración, cancelación y reprogramación.
// ============================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';

// UI
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';

// Icons
import {
  XCircle,
  Search,
  Star,
  CalendarPlus,
  Clock,
  UserRound,
} from 'lucide-react';

// Dialogs
import CancellationReasonDialog from '@/components/pages/dialogs/CancellationReasonDialog';
import ValoracionDialog from '@/components/pages/dialogs/ValoracionDialog';
import ReprogramarCitaDialog from '@/components/pages/dialogs/ReprogramarCitaDialog';
import HistorialReprogramacionesDrawer from '@/components/pages/dialogs/HistorialReprogramacionesDrawer';
import type { ReprogramacionHistorial } from '@/components/types/typeCalendar';

// Compartidos
import Pagination from '@/components/pages/shared/Pagination';

// Hooks
import { useCitasPorCliente } from '@/components/hooks/useCitas';

// ============================================================
// Config & Animations
// ============================================================

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const PAGE_SIZE_STORAGE_KEY = 'bookoll:client-appointments:pageSize';

const readStoredPageSize = () => {
  if (typeof window === 'undefined') return DEFAULT_PAGE_SIZE;
  const stored = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
  return PAGE_SIZE_OPTIONS.includes(stored) ? stored : DEFAULT_PAGE_SIZE;
};

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

// ============================================================
// Types
// ============================================================

interface Appointment {
  id_cita: string;
  id_empresa: string | null;
  id_servicio: string;
  duracion_minutos: number;
  fecha_cita: string;
  hora_cita: string;
  hora_fin: string;
  estado_cita: string;
  nombre_servicio: string | null;
  nombre_empresa: string | null;
  nombre_completo: string | null;
  precio: number | null;
  tiene_valoracion: boolean;
  puntuacion_cita: number | null;
  trabajadores_en_cita: { id_usuario: string; nombre: string }[] | null;
  reprogramada_cliente: boolean;
  fecha_anterior: string | null;
  hora_anterior: string | null;
  historial_reprogramaciones: ReprogramacionHistorial[] | null;
  logo_empresa: string | null;
  reservas_online_citas?: boolean;
  plan_incluye_reservas?: boolean;
}

// ============================================================
// Helpers
// ============================================================

const formatTime = (time: string) => (time ? time.slice(0, 5) : '');

const formatFechaCorta = (fecha: string) =>
  new Date(fecha).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
  });

const estadoBadge = (estado: string) =>
  ({
    confirmado:
      'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800',
    completado:
      'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800',
    cancelado:
      'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
    anulado:
      'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800',
  }[estado] ||
  'bg-gray-50 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700');

const estadoBorderLeft = (estado: string) =>
  ({
    confirmado: 'border-l-blue-500 dark:border-l-blue-400',
    completado: 'border-l-green-500 dark:border-l-green-400',
    cancelado: 'border-l-red-500 dark:border-l-red-400',
    anulado: 'border-l-yellow-500 dark:border-l-yellow-400',
  }[estado] || 'border-l-gray-300 dark:border-l-gray-600');

// Fondo suave por estado — mismo esquema de color que Appointments.tsx
// (getEstadoCardBg), para que las tarjetas de empresa y cliente coincidan.
const estadoCardBg = (estado: string) =>
  ({
    confirmado: 'bg-blue-50 dark:bg-blue-950/40',
    completado: 'bg-green-50 dark:bg-green-950/40',
    cancelado: 'bg-red-50 dark:bg-red-950/40',
    anulado: 'bg-yellow-50 dark:bg-yellow-950/40',
  }[estado] || 'bg-white dark:bg-[#1c1c1e]');

const estadoLabel = (estado: string) =>
  ({
    confirmado: 'Confirmada',
    completado: 'Completada',
    cancelado: 'Cancelada',
    anulado: 'Anulada',
  }[estado] || estado);

// Volver a reservar: además del flag de la empresa, el plan efectivo debe
// incluir "Reservas online". El !== false evita ocultar el botón mientras el
// campo no llegue todavía desde la vista.
const canRebook = (cita: Appointment) =>
  ['completado', 'cancelado', 'anulado'].includes(cita.estado_cita) &&
  !!cita.reservas_online_citas &&
  cita.plan_incluye_reservas !== false;

// Puede cancelar: confirmada y futura
const puedeCancelar = (cita: Appointment) =>
  cita.estado_cita === 'confirmado' &&
  new Date(`${cita.fecha_cita}T${cita.hora_cita}`) > new Date();

// Puede reprogramar: además, que no la haya movido ya
const puedeReprogramar = (cita: Appointment) =>
  puedeCancelar(cita) &&
  !cita.reprogramada_cliente &&
  cita.reservas_online_citas &&
  cita.plan_incluye_reservas !== false;

// Trabajador(es) que atienden la cita. La RPC ya los devuelve; si hay más
// de uno se listan separados por coma.
const nombreTrabajadores = (cita: { trabajadores_en_cita: { nombre: string }[] | null }) =>
  (cita.trabajadores_en_cita ?? [])
    .map((t) => t.nombre?.trim())
    .filter(Boolean)
    .join(', ');

// Fue reprogramada (por cliente o trabajador): hay al menos un movimiento
const fueReprogramada = (cita: Appointment) =>
  Boolean(cita.historial_reprogramaciones?.length);

// Movimiento más reciente del historial (el array viene DESC por fecha_creacion)
const ultimaReprogramacion = (cita: Appointment) =>
  cita.historial_reprogramaciones?.[0] ?? null;

// ============================================================
// Component
// ============================================================

const ClientAppointments = () => {
  const navigate = useNavigate();
  const userEmail = localStorage.getItem('email');
  const {
    appointments: rawAppointments,
    loading,
    fetchAppointments,
  } = useCitasPorCliente(userEmail);

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [allAppointments, setAllAppointments] = useState<Appointment[]>([]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(readStoredPageSize);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('todos');
  const [filterValoracion, setFilterValoracion] = useState('todos');

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedCita, setSelectedCita] = useState<string | null>(null);

  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [selectedRatingCita, setSelectedRatingCita] = useState<string | null>(
    null
  );

  // ── Reprogramación ───────────────────────────────────────────
  const [reprogramarOpen, setReprogramarOpen] = useState(false);
  const [citaReprogramar, setCitaReprogramar] = useState<Appointment | null>(
    null
  );

  const [motivo, setMotivo] = useState('');

  // ── Historial de reprogramaciones ───────────────────────────
  const [historialOpen, setHistorialOpen] = useState(false);
  const [citaHistorial, setCitaHistorial] = useState<Appointment | null>(
    null
  );
  const [showAllEstados, setShowAllEstados] = useState(false);

  const [searchParams] = useSearchParams();
  const listTopRef = useRef<HTMLDivElement | null>(null);

  // ── Fetch inicial ────────────────────────────────────────────
  useEffect(() => {
    if (!rawAppointments) return;
    setAllAppointments(rawAppointments as Appointment[]);
    setAppointments(rawAppointments as Appointment[]);
  }, [rawAppointments]);

  useEffect(() => {
    const estado = searchParams.get('estado');
    const valoracion = searchParams.get('valoracion');
    if (estado) setFilterEstado(estado);
    if (valoracion) setFilterValoracion(valoracion);
  }, [searchParams]);

  // ── Filtro ───────────────────────────────────────────────────
  useEffect(() => {
    let filtered = [...allAppointments];

    if (!searchParams.get('valoracion') && filterEstado !== 'completado') {
      setFilterValoracion('todos');
    }

    if (searchTerm.trim()) {
      const t = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          (c.nombre_servicio ?? '').toLowerCase().includes(t) ||
          (c.nombre_empresa ?? '').toLowerCase().includes(t) ||
          nombreTrabajadores(c).toLowerCase().includes(t) ||
          (c.nombre_completo ?? '').toLowerCase().includes(t)
      );
    }

    if (filterEstado !== 'todos') {
      filtered = filtered.filter(
        (c) => c.estado_cita.toLowerCase() === filterEstado
      );
    }

    if (filterEstado === 'completado') {
      if (filterValoracion === 'valoradas')
        filtered = filtered.filter((c) => c.tiene_valoracion);
      if (filterValoracion === 'sin_valorar')
        filtered = filtered.filter((c) => !c.tiene_valoracion);
    }

    setAppointments(filtered);
    setPage(1);
  }, [searchTerm, filterEstado, filterValoracion, allAppointments]);

  // ── Paginación ───────────────────────────────────────────────
  const totalItems = appointments.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
    if (typeof window !== 'undefined') {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
    }
  }, [pageSize]);

  const currentPageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = Math.min(start + pageSize, totalItems);
    return appointments.slice(start, end);
  }, [appointments, page, pageSize, totalItems]);

  // ── Loading ──────────────────────────────────────────────────
  if (loading)
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );

  // ── Render ───────────────────────────────────────────────────
  return (
    <motion.div
      className="w-full space-y-6 pb-28 lg:pb-0"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
        <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
          Historial de reservas
        </span>
      </motion.div>

      {/* Buscador + Filtros */}
      <Card className={`rounded-2xl ${GLASS_CARD}`}>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="relative w-full">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
            <Input
              placeholder="Buscar cita..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 rounded-xl text-sm w-full dark:bg-white/5 dark:border-white/10 dark:text-white"
            />
          </div>

          {/* Filtro estado */}
          {(() => {
            const ESTADOS = [
              ['todos', 'Todas'],
              ['confirmado', 'Confirmadas'],
              ['completado', 'Completadas'],
              ['cancelado', 'Canceladas'],
              ['anulado', 'Anuladas'],
            ] as const;
            const VISIBLE = 3;
            const visibles = showAllEstados ? ESTADOS : ESTADOS.slice(0, VISIBLE);
            const hidden = ESTADOS.length - VISIBLE;

            return (
              <div className="flex flex-wrap gap-2">
                {visibles.map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => setFilterEstado(value)}
                    className={`px-3 py-1.5 text-xs rounded-full border whitespace-nowrap transition-colors ${
                      filterEstado === value
                        ? 'border-[#103254] dark:border-blue-400 bg-[#103254]/5 dark:bg-blue-400/10 text-[#103254] dark:text-blue-300'
                        : 'border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {label}
                  </button>
                ))}
                {hidden > 0 && (
                  <button
                    onClick={() => setShowAllEstados((v) => !v)}
                    className="px-3 py-1.5 text-xs rounded-full border whitespace-nowrap border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400"
                  >
                    {showAllEstados ? 'Ver menos' : `+${hidden}`}
                  </button>
                )}
              </div>
            );
          })()}

          {/* Filtro valoración */}
          {filterEstado === 'completado' && (
            <div className="pt-3 border-t border-gray-100 dark:border-white/10">
              <div className="flex flex-wrap gap-2">
                {[
                  ['todos', 'Todas'],
                  ['valoradas', 'Valoradas ⭐'],
                  ['sin_valorar', 'Sin valorar'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    onClick={() => setFilterValoracion(value)}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                      filterValoracion === value
                        ? 'border-yellow-500 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400'
                        : 'border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sin datos */}
      {appointments.length === 0 ? (
        <motion.div
          variants={itemVariants}
          className={`relative isolate overflow-hidden rounded-2xl ${GLASS_CARD} px-6 py-10 flex flex-col items-center text-center`}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-[#2170b8]/10 dark:bg-[#2170b8]/15 blur-3xl"
          />
          <span className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#2170b8] to-[#1e5a8a] text-white shadow-[0_8px_20px_-6px_rgba(30,90,138,0.55),inset_0_1px_0_rgba(255,255,255,0.22)]">
            <CalendarPlus className="w-6 h-6" strokeWidth={2} />
          </span>
          <p className="mt-4 text-base font-semibold text-gray-900 dark:text-white">
            Aún no tienes citas
          </p>
          <p className="mt-1 max-w-xs text-sm text-gray-500 dark:text-gray-400">
            Cuando reserves una cita aparecerá aquí con su estado y todos los detalles.
          </p>
          <motion.button
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate('/panel-cliente/explore')}
            className="mt-5 inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-white bg-[#103254] hover:bg-[#0d2742]"
          >
            <Search className="w-4 h-4" />
            Explorar negocios
          </motion.button>
        </motion.div>
      ) : (
        <>
          <div ref={listTopRef} />

          {/* Cards */}
          <div className="grid grid-cols-1 gap-3 w-full">
            {currentPageData.map((cita) => (
              <motion.div key={cita.id_cita} variants={itemVariants}>
                <Card
                  className={`rounded-xl ${GLASS_CARD} border-l-4 ${estadoBorderLeft(
                    cita.estado_cita
                  )} ${estadoCardBg(cita.estado_cita)}`}
                >
                <CardContent className="p-4">
                {/* ── Móvil: layout original (columna info + columna botones) ── */}
                <div className="flex gap-3 sm:hidden">
                  <div className="shrink-0">
                    {cita.logo_empresa ? (
                      <img
                        src={cita.logo_empresa}
                        alt={cita.nombre_empresa ?? 'Empresa'}
                        className="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-white/10"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#103254]/10 dark:bg-blue-400/10 flex items-center justify-center text-[#103254] dark:text-blue-300 font-semibold">
                        {(cita.nombre_empresa ?? 'E').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className="flex-1 flex flex-col gap-1.5 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-white leading-tight">
                      {cita.nombre_empresa}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {cita.nombre_servicio}
                    </p>
                    {nombreTrabajadores(cita) && (
                      <p className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        <UserRound className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{nombreTrabajadores(cita)}</span>
                      </p>
                    )}
                    {cita.estado_cita === 'completado' &&
                      cita.tiene_valoracion &&
                      cita.puntuacion_cita !== null && (
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star
                              key={n}
                              className={`w-3.5 h-3.5 ${
                                n <= (cita.puntuacion_cita ?? 0)
                                  ? 'text-yellow-400 fill-yellow-400'
                                  : 'text-gray-300 dark:text-gray-600'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                      {(cita.precio ?? 0).toFixed(2)} €
                    </p>
                    {fueReprogramada(cita) && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-xs text-gray-400 dark:text-gray-500 line-through">
                          {formatFechaCorta(
                            ultimaReprogramacion(cita)!.fecha_anterior
                          )}{' '}
                          —{' '}
                          {formatTime(
                            ultimaReprogramacion(cita)!.hora_anterior
                          )}
                        </p>
                        {(cita.historial_reprogramaciones?.length ?? 0) >
                          1 && (
                          <button
                            onClick={() => {
                              setCitaHistorial(cita);
                              setHistorialOpen(true);
                            }}
                            className="text-[11px] font-semibold text-[#103254] dark:text-blue-400 underline underline-offset-2"
                          >
                            Ver historial
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="w-1/3 shrink-0 flex flex-col justify-center gap-2">
                    {puedeReprogramar(cita) && (
                      <Button
                        onClick={() => {
                          setCitaReprogramar(cita);
                          setReprogramarOpen(true);
                        }}
                        variant="outline"
                        size="sm"
                        className="w-full rounded-lg text-xs border-[#103254]/30 text-[#103254] hover:bg-[#103254]/5 dark:text-blue-400 dark:border-blue-800"
                      >
                        <Clock className="w-3.5 h-3.5 mr-1 shrink-0" /> Cambiar
                      </Button>
                    )}
                    {puedeCancelar(cita) && (
                      <Button
                        onClick={() => {
                          setSelectedCita(cita.id_cita);
                          setMotivo('');
                          setCancelDialogOpen(true);
                        }}
                        variant="outline"
                        size="sm"
                        className="w-full rounded-lg text-xs border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1 shrink-0" /> Cancelar
                      </Button>
                    )}
                    {cita.estado_cita === 'completado' &&
                      !cita.tiene_valoracion && (
                        <Button
                          onClick={() => {
                            setSelectedRatingCita(cita.id_cita);
                            setRatingDialogOpen(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="w-full rounded-lg text-xs border-yellow-500 text-yellow-700 hover:bg-yellow-50 dark:text-yellow-400 dark:border-yellow-600"
                        >
                          <Star className="w-3.5 h-3.5 mr-1 shrink-0 text-yellow-500" />{' '}
                          Valorar
                        </Button>
                      )}
                    {canRebook(cita) && cita.id_empresa && (
                      <Button
                        onClick={() =>
                          navigate(`/client-booking/${cita.id_empresa}`, {
                            state: { showBackButton: true },
                          })
                        }
                        variant="outline"
                        size="sm"
                        className="rounded-lg text-xs border-[#103254]/30 text-[#103254] hover:bg-[#103254]/5 dark:text-blue-400 dark:border-blue-800"
                      >
                        <CalendarPlus className="w-3.5 h-3.5 mr-1.5" /> Reservar
                      </Button>
                    )}
                  </div>
                </div>

                {/* Fecha y hora — móvil, a todo el ancho */}
                <p className="sm:hidden mt-2 text-xs text-gray-400 dark:text-gray-500">
                  {new Date(cita.fecha_cita).toLocaleDateString('es-ES', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  })}{' '}
                  — {formatTime(cita.hora_cita)} –{' '}
                  {formatTime(cita.hora_fin)}
                </p>

                {/* ── Desktop: layout horizontal compacto ── */}
                <div className="hidden sm:flex items-center gap-4">
                  {/* Logo empresa */}
                  <div className="shrink-0">
                    {cita.logo_empresa ? (
                      <img
                        src={cita.logo_empresa}
                        alt={cita.nombre_empresa ?? 'Empresa'}
                        className="w-12 h-12 rounded-full object-cover border border-gray-200 dark:border-white/10"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#103254]/10 dark:bg-blue-400/10 flex items-center justify-center text-[#103254] dark:text-blue-300 font-semibold">
                        {(cita.nombre_empresa ?? 'E').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  {/* Info principal */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-sm font-semibold text-gray-800 dark:text-white leading-tight">
                        {cita.nombre_empresa}
                      </h3>
                      <span
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${estadoBadge(
                          cita.estado_cita
                        )}`}
                      >
                        {estadoLabel(cita.estado_cita)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {cita.nombre_servicio}
                      {nombreTrabajadores(cita) && (
                        <span className="inline-flex items-center gap-1 ml-2 text-gray-400 dark:text-gray-500">
                          <UserRound className="w-3 h-3 shrink-0" />
                          {nombreTrabajadores(cita)}
                        </span>
                      )}
                    </p>
                    {cita.estado_cita === 'completado' &&
                      cita.tiene_valoracion &&
                      cita.puntuacion_cita !== null && (
                        <div className="flex gap-0.5 mt-1">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star
                              key={n}
                              className={`w-3 h-3 ${
                                n <= (cita.puntuacion_cita ?? 0)
                                  ? 'text-yellow-400 fill-yellow-400'
                                  : 'text-gray-300 dark:text-gray-600'
                              }`}
                            />
                          ))}
                        </div>
                      )}
                  </div>

                  {/* Fecha y hora */}
                  <div className="shrink-0 text-right hidden md:block">
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                      {new Date(cita.fecha_cita).toLocaleDateString('es-ES', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                      })}
                    </p>
                    {fueReprogramada(cita) && (
                      <div className="flex items-center justify-end gap-1.5 mt-0.5 flex-wrap">
                        <span className="text-xs text-gray-400 dark:text-gray-500 line-through">
                          {formatFechaCorta(
                            ultimaReprogramacion(cita)!.fecha_anterior
                          )}{' '}
                          —{' '}
                          {formatTime(
                            ultimaReprogramacion(cita)!.hora_anterior
                          )}
                        </span>
                        {(cita.historial_reprogramaciones?.length ?? 0) >
                          1 && (
                          <button
                            onClick={() => {
                              setCitaHistorial(cita);
                              setHistorialOpen(true);
                            }}
                            className="text-[11px] font-semibold text-[#103254] dark:text-blue-400 underline underline-offset-2"
                          >
                            Ver historial
                          </button>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-end gap-1 mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                      <Clock className="w-3 h-3" />
                      {formatTime(cita.hora_cita)} –{' '}
                      {formatTime(cita.hora_fin)}
                    </div>
                  </div>

                  {/* Precio */}
                  <div className="shrink-0 text-right w-16">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                      {(cita.precio ?? 0).toFixed(2)} €
                    </p>
                  </div>

                  {/* Botones compactos */}
                  <div className="shrink-0 flex items-center gap-2">
                    {puedeReprogramar(cita) && (
                      <Button
                        onClick={() => {
                          setCitaReprogramar(cita);
                          setReprogramarOpen(true);
                        }}
                        variant="outline"
                        size="sm"
                        className="rounded-lg text-xs border-[#103254]/30 text-[#103254] hover:bg-[#103254]/5 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/20"
                      >
                        <Clock className="w-3.5 h-3.5 mr-1.5" /> Cambiar hora
                      </Button>
                    )}
                    {puedeCancelar(cita) && (
                      <Button
                        onClick={() => {
                          setSelectedCita(cita.id_cita);
                          setMotivo('');
                          setCancelDialogOpen(true);
                        }}
                        variant="outline"
                        size="sm"
                        className="rounded-lg text-xs border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1.5" /> Cancelar
                      </Button>
                    )}
                    {cita.estado_cita === 'completado' &&
                      !cita.tiene_valoracion && (
                        <Button
                          onClick={() => {
                            setSelectedRatingCita(cita.id_cita);
                            setRatingDialogOpen(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="rounded-lg text-xs border-yellow-300 text-yellow-700 hover:bg-yellow-50 dark:border-yellow-700 dark:text-yellow-400 dark:hover:bg-yellow-900/20"
                        >
                          <Star className="w-3.5 h-3.5 mr-1.5 text-yellow-500" />{' '}
                          Valorar
                        </Button>
                      )}
                    {canRebook(cita) && cita.id_empresa && (
                      <Button
                        onClick={() =>
                          navigate(`/client-booking/${cita.id_empresa}`, {
                            state: { showBackButton: true },
                          })
                        }
                        variant="outline"
                        size="sm"
                        className="rounded-lg text-xs border-[#103254]/30 text-[#103254] hover:bg-[#103254]/5 dark:text-blue-400 dark:border-blue-800"
                      >
                        <CalendarPlus className="w-3.5 h-3.5 mr-1.5" /> Reservar
                      </Button>
                    )}
                  </div>
                </div>
                </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Paginación — componente compartido, igual que en el resto de listados */}
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            itemLabel="citas"
          />
        </>
      )}

      {/* Dialog cancelación */}
      {selectedCita && (
        <CancellationReasonDialog
          idCita={selectedCita}
          motivo={motivo}
          setMotivo={setMotivo}
          open={cancelDialogOpen}
          onCancel={() => setCancelDialogOpen(false)}
          onCloseAll={() => {
            setCancelDialogOpen(false);
            fetchAppointments();
          }}
          incrementReload={fetchAppointments}
        />
      )}

      {/* Dialog valoración */}
      {selectedRatingCita && (
        <ValoracionDialog
          idCita={selectedRatingCita}
          open={ratingDialogOpen}
          onCloseAll={() => {
            setRatingDialogOpen(false);
            fetchAppointments();
          }}
        />
      )}

      {/* Dialog reprogramación */}
      {citaReprogramar && (
        <ReprogramarCitaDialog
          open={reprogramarOpen}
          onOpenChange={(open) => {
            setReprogramarOpen(open);
            if (!open) setCitaReprogramar(null);
          }}
          cita={{
            id_cita: citaReprogramar.id_cita,
            id_servicio: citaReprogramar.id_servicio,
            duracion_minutos: citaReprogramar.duracion_minutos,
            hora_cita: citaReprogramar.hora_cita,
            hora_fin: citaReprogramar.hora_fin,
            id_trabajador:
              citaReprogramar.trabajadores_en_cita?.[0]?.id_usuario ?? null,
            nombre_servicio: citaReprogramar.nombre_servicio,
          }}
          onReprogramada={fetchAppointments}
        />
      )}

      {/* Drawer historial de reprogramaciones */}
      {citaHistorial && (
        <HistorialReprogramacionesDrawer
          open={historialOpen}
          onOpenChange={(open) => {
            setHistorialOpen(open);
            if (!open) setCitaHistorial(null);
          }}
          historial={citaHistorial.historial_reprogramaciones ?? []}
          nombreServicio={citaHistorial.nombre_servicio}
        />
      )}
    </motion.div>
  );
};

export default ClientAppointments;